import React from "react";
import { styled } from "@mui/material/styles";
import Santa from "../../assets/santa-claus-icon.png";
import Sled from "../../assets/sled.svg";
import ChristmasTree from "../../assets/christmas_tree.svg";
import FireworkOne from "../../assets/firework-1.svg";
import FireworkTwo from "../../assets/firework-2.svg";
import Smoke from "../../assets/smoke.svg";
import ArcDeTriomphe from "../../assets/arc-de-triomphe.png";
import Statue from "../../assets/statue.png";
import Note from "../../assets/note.svg";
import BlackAndWhite from "../../assets/blackAndWhite.svg";
import Sepia from "../../assets/sepia.png";
import Rainy from "../../assets/rainy.svg";
import Pickleball from "../../assets/pickleball.png";
import VideogameAssetIcon from "@mui/icons-material/VideogameAsset";
import SportsBasketballIcon from "@mui/icons-material/SportsBasketball";
import { Videocam, Accessibility, Group, ViewInAr } from "@mui/icons-material";
import {
  OPACITY_GRID_EVENT,
  TILE_SIZE,
  TILE_SIZE_RESPONSIVE,
} from "./constants";
import { useSelector } from "react-redux";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";

const PREFIX = "ConfiguratorIcons";

const classes = {
  root: `${PREFIX}-root`,
  iconPosition: `${PREFIX}-iconPosition`,
  treeIconPosition: `${PREFIX}-treeIconPosition`,
  treeIconNeutral: `${PREFIX}-treeIconNeutral`,
  teqballSquare: `${PREFIX}-teqballSquare`,
  songIconPosition: `${PREFIX}-songIconPosition`,
  filterIconPosition: `${PREFIX}-filterIconPosition`,
  redLine: `${PREFIX}-redLine`,
  blueLine: `${PREFIX}-blueLine`,
  whiteLine: `${PREFIX}-whiteLine`,
  greenLine: `${PREFIX}-greenLine`,
  orangeLine: `${PREFIX}-orangeLine`,
};

const ConfiguratorIconsContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      position: "relative",
      width: "100%",
      height: "100%",
      display: "flex",
      justifyContent: "center",
    },
    [`& .${classes.iconPosition}`]: {
      position: "absolute",
      top: "50%",
      left: "50%",
    },
    [`& .${classes.treeIconNeutral}`]: {
      position: "inherit",
      maxWidth: "38px",
    },
    [`& .${classes.treeIconPosition}`]: {
      [theme.breakpoints.between("xs", "sm")]: {
        width: "22px",
        height: "22px",
      },
      objectFit: "contain",
      width: "30px",
      height: "30px",
      position: "absolute",
      top: "50%",
      left: "50%",
    },
    [`& .${classes.teqballSquare}`]: {
      backgroundColor: "cyan",
      [theme.breakpoints.between("xs", "sm")]: {
        height: `calc(${TILE_SIZE_RESPONSIVE}px* 0.6)`,
        width: `calc(${TILE_SIZE_RESPONSIVE}px* 0.6)`,
      },
      height: `calc(${TILE_SIZE}px * 0.6)`,
      width: `calc(${TILE_SIZE}px* 0.6)`,
    },
    [`& .${classes.songIconPosition}`]: {
      [theme.breakpoints.between("xs", "sm")]: {
        width: "18px",
        top: "2px",
        left: "2px",
      },
      top: "10px",
      left: "10px",
      width: "24px",
      position: "absolute",
      color: "#D3AF5F",
    },
    [`& .${classes.filterIconPosition}`]: {
      [theme.breakpoints.between("xs", "sm")]: {
        width: "18px",
        top: "2px",
        right: "2px",
      },
      width: "24px",
      top: "10px",
      right: "10px",
      position: "absolute",
      color: "white",
    },
    [`& .${classes.redLine}`]: {
      width: "70%",
      height: "2px",
      transform: "translateY(10px)",
      backgroundColor: "red",
    },
    [`& .${classes.blueLine}`]: {
      width: "70%",
      height: "2px",
      transform: "translateY(10px)",
      backgroundColor: "blue",
    },
    [`& .${classes.whiteLine}`]: {
      width: "70%",
      height: "2px",
      transform: "translateY(10px)",
      backgroundColor: "white",
    },
    [`& .${classes.greenLine}`]: {
      width: "70%",
      height: "2px",
      transform: "translateY(10px)",
      backgroundColor: "green",
    },
    [`& .${classes.orangeLine}`]: {
      width: "70%",
      height: "2px",
      transform: "translateY(10px)",
      backgroundColor: "orange",
    },
  };
});

const ConfiguratorIcons = ({ orientation, side, isForDialog, tile }) => {
  const isNeutral = side === "neutralPosition";

  const currentLayout = useSelector((state) => state.configurator?.layout);

  const santasIcon = (
    <img
      id="santa"
      className={isNeutral ? classes.treeIconNeutral : classes.treeIconPosition}
      alt="santa-icon"
      src={Santa}
      style={
        isForDialog
          ? { width: "25px", height: "25px" }
          : {
              transform: !isNeutral ? `translate(-50%, -50%)` : "",
              opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
            }
      }
    />
  );

  const rotationValue = {
    north: 0,
    east: 90,
    south: 180,
    west: 270,
  };

  let specificAngle = rotationValue[orientation.rotation];

  const iconToReturn = {
    ipad: isForDialog ? <div className={classes.redLine} /> : null,
    screen: isForDialog ? <div className={classes.blueLine} /> : null,
    frame: isForDialog ? <div className={classes.whiteLine} /> : null,
    fazlogo: isForDialog ? <div className={classes.greenLine} /> : null,
    designlogo: isForDialog ? <div className={classes.orangeLine} /> : null,
    spawn: (
      <Videocam
        className={!isNeutral ? classes.iconPosition : ""}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                transform: !isNeutral
                  ? `translate(-50%, -50%) rotate(${specificAngle - 90}deg)`
                  : `rotate(${specificAngle - 90}deg)`,
                opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
                top:
                  orientation.offset === "top"
                    ? "0"
                    : orientation.offset === "bottom"
                    ? "100%"
                    : "50%",
                left:
                  orientation.offset === "left"
                    ? "0"
                    : orientation.offset === "right"
                    ? "100%"
                    : "50%",
              }
        }
      />
    ),
    cyril: (
      <Accessibility
        className={!isNeutral ? classes.iconPosition : ""}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                transform: !isNeutral
                  ? `translate(-50%, -50%) rotate(${
                      rotationValue[orientation.rotation] || 0
                    }deg)`
                  : `rotate(${rotationValue[orientation.rotation] || 0}deg)`,
                opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
              }
        }
      />
    ),
    neymar: (
      <Accessibility
        className={!isNeutral ? classes.iconPosition : ""}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                transform: !isNeutral
                  ? `translate(-50%, -50%) rotate(${
                      rotationValue[orientation.rotation] || 0
                    }deg)`
                  : `rotate(${rotationValue[orientation.rotation] || 0}deg)`,
                opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
              }
        }
      />
    ),
    alainroche: (
      <Accessibility
        className={!isNeutral ? classes.iconPosition : ""}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                transform: !isNeutral
                  ? `translate(-50%, -50%) rotate(${
                      rotationValue[orientation.rotation] || 0
                    }deg)`
                  : `rotate(${rotationValue[orientation.rotation] || 0}deg)`,
                opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
              }
        }
      />
    ),
    santa1: santasIcon,
    santa2: santasIcon,
    // basket1: (
    //   <SportsBasketballIcon
    //     className={!isNeutral ? classes.iconPosition : ""}
    //     style={
    //       isForDialog
    //         ? { width: "25px", height: "25px" }
    //         : {
    //             transform: !isNeutral
    //               ? `translate(-50%, -50%) rotate(${
    //                   rotationValue[orientation.rotation] || 0
    //                 }deg)`
    //               : `rotate(${rotationValue[orientation.rotation] || 0}deg)`,
    //             opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
    //           }
    //     }
    //   />
    // ),
    basket2: (
      <SportsBasketballIcon
        className={!isNeutral ? classes.iconPosition : ""}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                transform: !isNeutral
                  ? `translate(-50%, -50%) rotate(${
                      rotationValue[orientation.rotation] || 0
                    }deg)`
                  : `rotate(${rotationValue[orientation.rotation] || 0}deg)`,
                opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
              }
        }
      />
    ),
    pickleball: (
      <img
        id="pickleball"
        className={
          isNeutral ? classes.treeIconNeutral : classes.treeIconPosition
        }
        alt="pickleball-icon"
        src={Pickleball}
        style={
          isForDialog
            ? { width: "40px", height: "40px" }
            : {
                transform: !isNeutral ? `translate(-50%, -50%)` : "",
                opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
                width: "40px",
                height: "40px",
              }
        }
      />
    ),
    football: (
      <SportsSoccerIcon
        className={!isNeutral ? classes.iconPosition : ""}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                transform: !isNeutral
                  ? `translate(-50%, -50%) rotate(${
                      rotationValue[orientation.rotation] || 0
                    }deg)`
                  : `rotate(${rotationValue[orientation.rotation] || 0}deg)`,
                opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
              }
        }
      />
    ),
    arcade: (
      <VideogameAssetIcon
        className={!isNeutral ? classes.iconPosition : ""}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                transform: !isNeutral
                  ? `translate(-50%, -50%) rotate(${
                      rotationValue[orientation.rotation] || 0
                    }deg)`
                  : `rotate(${rotationValue[orientation.rotation] || 0}deg)`,
                opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
              }
        }
      />
    ),
    sled: (
      <img
        id="sled"
        className={
          isNeutral ? classes.treeIconNeutral : classes.treeIconPosition
        }
        alt="sled-icon"
        src={Sled}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                transform: !isNeutral ? `translate(-50%, -50%)` : "",
                opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
              }
        }
      />
    ),
    custom: (
      <ViewInAr
        className={
          isNeutral ? classes.treeIconNeutral : classes.treeIconPosition
        }
        alt="viewInAR-icon"
        style={
          isForDialog
            ? {
                width: "25px",
                height: "25px",
                fill: "D3AF5F",
              }
            : {
                transform: `translate(-50%, -50%)`,
                opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
              }
        }
      />
    ),
    christmastree: (
      <img
        id="christmasTree"
        className={
          isNeutral ? classes.treeIconNeutral : classes.treeIconPosition
        }
        alt="christmas-tree-icon"
        src={ChristmasTree}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                transform: !isNeutral ? `translate(-50%, -50%)` : "",
                opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
              }
        }
      />
    ),
    fw1: (
      <img
        id="fw1"
        className={
          isNeutral ? classes.treeIconNeutral : classes.treeIconPosition
        }
        alt="firework-1-icon"
        src={FireworkOne}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                transform: !isNeutral ? `translate(-50%, -50%)` : "",
                opacity: currentLayout === "event" ? "1" : OPACITY_GRID_EVENT,
              }
        }
      />
    ),
    fw2: (
      <img
        id="fw2"
        className={
          isNeutral ? classes.treeIconNeutral : classes.treeIconPosition
        }
        alt="firework-2-icon"
        src={FireworkTwo}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                transform: !isNeutral ? `translate(-50%, -50%)` : "",
                opacity: currentLayout === "event" ? "1" : OPACITY_GRID_EVENT,
              }
        }
      />
    ),
    smoke: (
      <img
        id="smoke"
        className={
          isNeutral ? classes.treeIconNeutral : classes.treeIconPosition
        }
        alt="smoke-icon"
        src={Smoke}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                transform: !isNeutral ? `translate(-50%, -50%)` : "",
                opacity: currentLayout === "event" ? "1" : OPACITY_GRID_EVENT,
              }
        }
      />
    ),
    teqball: (
      <div
        className={classes.teqballSquare}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : isNeutral
            ? {}
            : side === "north" || side === "south"
            ? {
                position: "absolute",
                top: side === "north" ? "0" : "auto",
                right: "50%",
                bottom: side === "south" ? "0" : "auto",
                left: "50%",
                zIndex: 999,
                transform:
                  side === "north" || side === "south"
                    ? "translateX(-50%)"
                    : "translateY(-50%)",
                opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
              }
            : {
                position: "absolute",
                top: "50%",
                right: side === "east" ? "0" : "auto",
                bottom: "50%",
                left: side === "west" ? "0" : "auto",
                zIndex: 999,
                transform:
                  side === "north" || side === "south"
                    ? "translateX(-50%)"
                    : "translateY(-50%)",
                opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
              }
        }
      />
    ),
    arcdetriomphe: (
      <img
        id="arcDeTriomphe"
        className={
          isNeutral ? classes.treeIconNeutral : classes.treeIconPosition
        }
        alt="arc-de-triomphe-icon"
        src={ArcDeTriomphe}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                transform: !isNeutral ? `translate(-50%, -50%)` : "",
                opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
              }
        }
      />
    ),
    jaures: (
      <img
        id="jaures"
        className={
          isNeutral ? classes.treeIconNeutral : classes.treeIconPosition
        }
        alt="jaures-icon"
        src={Statue}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                transform: !isNeutral ? `translate(-50%, -50%)` : "",
                opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
              }
        }
      />
    ),
    guest: (
      <Group
        className={!isNeutral ? classes.iconPosition : ""}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                transform: `translate(${
                  !isNeutral ? "-50%, -50%" : "0, 0"
                }) rotate(${rotationValue[orientation.rotation] || 0}deg)`,
                opacity: currentLayout !== "normal" ? OPACITY_GRID_EVENT : "1",
              }
        }
      />
    ),
  };

  const themeIcons = {
    sepia: (
      <img
        id="sepia"
        className={classes.filterIconPosition}
        alt="sepia-icon"
        src={Sepia}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                opacity: currentLayout === "theme" ? "1" : OPACITY_GRID_EVENT,
              }
        }
      />
    ),
    blackAndWhite: (
      <img
        id="blackAndWhite"
        className={classes.filterIconPosition}
        alt="blackAndWhite-icon"
        src={BlackAndWhite}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                opacity: currentLayout === "theme" ? "1" : OPACITY_GRID_EVENT,
              }
        }
      />
    ),
    rainy: (
      <img
        id="rainy"
        className={classes.filterIconPosition}
        alt="rainy-icon"
        src={Rainy}
        style={
          isForDialog
            ? { width: "25px", height: "25px" }
            : {
                opacity: currentLayout === "theme" ? "1" : OPACITY_GRID_EVENT,
              }
        }
      />
    ),
  };

  return (
    <ConfiguratorIconsContainer
      style={
        isNeutral || isForDialog
          ? {
              position: "inherit",
              width: "100%",
            }
          : {
              width: "100%",
              height: "100%",
              position: "absolute",
              top: "0",
              left: "0",
            }
      }
    >
      <div className={classes.root}>
        {orientation.iconUrl && orientation.type === "custom" ? (
          <img
            src={orientation.iconUrl}
            alt={`icon ${orientation.type}`}
            id={orientation.type}
            className={
              isNeutral ? classes.treeIconNeutral : classes.treeIconPosition
            }
            style={
              isForDialog
                ? { width: "25px", height: "25px" }
                : {
                    transform: !isNeutral ? `translate(-50%, -50%)` : "",
                    opacity:
                      currentLayout !== "normal"
                        ? OPACITY_GRID_EVENT
                        : (orientation.hasOwnProperty("startTile") &&
                            tile?.row !== orientation.startTile?.row) ||
                          (orientation.hasOwnProperty("startTile") &&
                            tile?.column !== orientation.startTile?.column)
                        ? "0.5"
                        : "1",
                  }
            }
          />
        ) : iconToReturn.hasOwnProperty(orientation.type) ? (
          iconToReturn[orientation.type]
        ) : null}
        {orientation.song && (
          <img
            id="note"
            className={classes.songIconPosition}
            alt="note-icon"
            src={Note}
            style={{
              opacity: currentLayout === "theme" ? "1" : OPACITY_GRID_EVENT,
            }}
          />
        )}
        {orientation.theme && (themeIcons[orientation.theme] || null)}
      </div>
    </ConfiguratorIconsContainer>
  );
};

export default ConfiguratorIcons;
